import { formatDistanceToNow } from 'date-fns';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { usePresence } from '@/hooks/useUserActivity';
import { useQuery } from '@tanstack/react-query';
import { fetchMinimalUsers, USERS_MINIMAL_KEY } from '@/hooks/useUsers';
import { getProfileImageUrl } from '@/components/access-control/utils/nameUtils';
import { Circle } from 'lucide-react';
import { useMemo } from 'react';

interface TeamPresenceBarProps {
  /** Max avatars to show before "+N" overflow chip */
  maxAvatars?: number;
}

const TeamPresenceBar = ({ maxAvatars = 6 }: TeamPresenceBarProps) => {
  const { data, isLoading } = usePresence();
  const { data: allUsers } = useQuery({
    queryKey: USERS_MINIMAL_KEY,
    queryFn: fetchMinimalUsers,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const onlineUsers = data?.online_users ?? [];
  const totalUsers = data?.total_users ?? 0;

  // Build a map of user_id -> user for profile image lookup
  const userMap = useMemo(() => {
    const map = new Map<number, any>();
    allUsers?.forEach((u: any) => map.set(u.id, u));
    return map;
  }, [allUsers]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <div className="h-2.5 w-2.5 rounded-full bg-muted animate-pulse" />
        <div className="h-4 w-24 rounded bg-muted animate-pulse" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-center gap-1.5">
        {/* Online indicator dot */}
        <Circle className="h-2.5 w-2.5 fill-green-500 text-green-500 shrink-0" />

        {/* Stacked avatars */}
        <div className="flex -space-x-2 mr-0.5">
          {onlineUsers.slice(0, maxAvatars).map((u) => {
            const initials =
              `${u.first_name?.[0] ?? ''}${u.last_name?.[0] ?? ''}`.toUpperCase() ||
              u.username?.[0]?.toUpperCase() ||
              '?';
            const lastSeen = u.last_seen_at
              ? formatDistanceToNow(new Date(u.last_seen_at), { addSuffix: true })
              : 'just now';

            return (
              <Tooltip key={u.user_id}>
                <TooltipTrigger asChild>
                  <Avatar className="h-7 w-7 border-2 border-background ring-1 ring-green-500/30 cursor-default">
                    <AvatarImage
                      src={getProfileImageUrl(userMap.get(u.user_id) || { id: u.user_id })}
                      alt={`${u.first_name} ${u.last_name}`}
                      className="object-cover"
                    />
                    <AvatarFallback className="bg-primary/15 text-primary text-[10px] font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  <p className="font-medium">{u.first_name} {u.last_name}</p>
                  <p className="text-muted-foreground">Active {lastSeen}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
          {onlineUsers.length > maxAvatars && (
            <Avatar className="h-7 w-7 border-2 border-background cursor-default">
              <AvatarFallback className="bg-muted text-muted-foreground text-[10px] font-semibold">
                +{onlineUsers.length - maxAvatars}
              </AvatarFallback>
            </Avatar>
          )}
        </div>

        {/* Count label */}
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {onlineUsers.length}/{totalUsers} online
        </span>
      </div>
    </TooltipProvider>
  );
};

export default TeamPresenceBar;
